import { DateString } from '../../../../../common/src/date_string.ts';
import { RepeatKind, SongListEntryType, SongListSchema, SongListSchemaEntry } from '../../../../../common/src/model.ts';

// Replaces the schema's row `rowIndex` with `update` of it; the row's type defaults to Song, and the updated row moves
// to the end of the rows.
function updateSchemaRow(
  schema: SongListSchema | undefined,
  rowIndex: number,
  update: (row: SongListSchemaEntry) => SongListSchemaEntry,
): SongListSchema {
  const row = { row: rowIndex, type: SongListEntryType.Song, ...schema?.rows.find(({ row }) => row === rowIndex) };
  return {
    ...schema,
    rows: [...(schema?.rows.filter(({ row }) => row !== rowIndex) ?? []), update(row)],
  };
}

export function setSongListSchemaLabel(
  schema: SongListSchema | undefined,
  rowIndex: number,
  label?: string,
): SongListSchema {
  return updateSchemaRow(schema, rowIndex, ({ label: _unused, ...rowNoLabel }) => ({
    ...rowNoLabel,
    ...(label && label !== '' && { label: label }),
  }));
}

// keeps Show as Hymn of the Month, which has effect only while the row is of type Song (isHymnOfTheMonthRow)
export function setSongListSchemaType(
  schema: SongListSchema | undefined,
  rowIndex: number,
  type: SongListEntryType,
): SongListSchema {
  return updateSchemaRow(schema, rowIndex, (row) => ({ ...row, type }));
}

export function setSongListSchemaRowAssigned(
  schema: SongListSchema | undefined,
  rowIndex: number,
  assigned: boolean,
): SongListSchema {
  return updateSchemaRow(schema, rowIndex, (row) => ({ ...row, type: SongListEntryType.Song, assigned }));
}

// stores `repeat` only for a repeating kind, with `repeatFrom`, the admin's current Sunday, as the first Sunday the kind
// applies to (issue #4192); drops both for Does not repeat. Show as Hymn of the Month stays on the row whatever the
// kind, so a starred row switched to another kind and back is starred again; it has effect only while the row is a
// Song-type Same-all-month row (isHymnOfTheMonthRow, issue #4210)
export function setSongListSchemaRepeat(
  schema: SongListSchema | undefined,
  rowIndex: number,
  kind: RepeatKind,
  currentSunday: DateString,
): SongListSchema {
  return updateSchemaRow(schema, rowIndex, ({ repeat: _repeat, repeatFrom: _repeatFrom, ...rowNoRepeat }) => ({
    ...rowNoRepeat,
    ...(kind !== RepeatKind.DoesNotRepeat && { repeat: kind, repeatFrom: currentSunday }),
  }));
}

export function setSongListSchemaShowAsHymnOfTheMonth(
  schema: SongListSchema | undefined,
  rowIndex: number,
  show: boolean,
): SongListSchema {
  return updateSchemaRow(schema, rowIndex, ({ showAsHymnOfTheMonth: _unused, ...rowNoStar }) => ({
    ...rowNoStar,
    ...(show && { showAsHymnOfTheMonth: true }),
  }));
}
